import type { ReportId } from "@/features/today"

import type {
  ModalShowState,
  ReportTheme,
} from "../persistence/modal-show-state"
import type { AppModalType } from "../types"
import { getAppNow } from "./get-app-now"

const MS_HOUR = 60 * 60 * 1000
const MS_DAY = 24 * MS_HOUR

const TYPE_COOLDOWN_MS = 3 * MS_DAY
const GROUP_COOLDOWN_MS = 24 * MS_HOUR
const STARS_HIGH_BALANCE_COOLDOWN_MS = 5 * MS_DAY
const REPORT_AFTER_V2_COOLDOWN_MS = 7 * MS_DAY
const STARS_LOW_BALANCE_THRESHOLD = 30

export type ReportThemeKey = ReportTheme

export const REPORT_THEME_PRIORITY: ReportThemeKey[] = [
  "soulmate_sketch",
  "astrology",
  "love_match",
  "astro_map",
  "aura_scan",
]

export const REPORT_THEME_TO_ID: Record<ReportThemeKey, ReportId> = {
  soulmate_sketch: "soulmate-sketch",
  astrology: "astrology",
  love_match: "love-match",
  astro_map: "astro-map",
  aura_scan: "aura-scan",
}

function themeModalType(theme: ReportThemeKey, variant: 1 | 2): AppModalType {
  const base =
    theme === "soulmate_sketch"
      ? "report_soulmate_sketch"
      : theme === "astrology"
        ? "report_astrology"
        : theme === "love_match"
          ? "report_love_match"
          : theme === "aura_scan"
            ? "report_aura_scan"
            : "report_astro_map"
  return `${base}_v${variant}` as AppModalType
}

function hoursPassed(fromIso: string | undefined, now: Date, ms: number): boolean {
  if (!fromIso) return true
  return now.getTime() - new Date(fromIso).getTime() >= ms
}

function typeCooldownOk(
  state: ModalShowState,
  type: AppModalType,
  now: Date,
): boolean {
  return hoursPassed(state.lastShownAtByType[type], now, TYPE_COOLDOWN_MS)
}

function groupCooldownOk(
  state: ModalShowState,
  group: "event" | "stars" | "reports",
  now: Date,
): boolean {
  return hoursPassed(state.lastShownAtByGroup[group], now, GROUP_COOLDOWN_MS)
}

export interface SelectAppModalInput {
  now?: Date
  state: ModalShowState
  /** null = stars branch skipped (credits not loaded yet). */
  credits: number | null
  /** Report ids the user already owns. */
  purchasedReportIds: ReadonlySet<ReportId>
  hasActiveEvent: boolean
}

/**
 * Highest-priority eligible modal, or null.
 * Priority: Event → report rotation → Stars.
 * Reports: all themes v1 in {@link REPORT_THEME_PRIORITY}, then the same
 * order at v2 (never v2 for a theme while another still needs v1). After
 * every remaining theme's v2, wait 7d from the last of those v2s, then
 * restart the full v1 pass (so the last theme, Aura, is not skipped).
 * Does not apply session/daily caps (caller does).
 */
export function selectAppModal({
  now = getAppNow(),
  state,
  credits,
  purchasedReportIds,
  hasActiveEvent,
}: SelectAppModalInput): AppModalType | null {
  if (hasActiveEvent && isEventEligible(state, now)) {
    return "event"
  }

  const report = pickNextReport(state, purchasedReportIds, now)
  if (report) return report

  if (credits !== null) {
    const stars = pickStarsType(state, credits, now)
    if (stars) return stars
  }

  return null
}

function isEventEligible(state: ModalShowState, now: Date): boolean {
  return (
    typeCooldownOk(state, "event", now) && groupCooldownOk(state, "event", now)
  )
}

/**
 * Cross-theme report rotation:
 * all unpurchased themes v1 in {@link REPORT_THEME_PRIORITY}, then the same
 * order at v2. After the last v2, wait 7d and restart the full v1 pass.
 * Per-theme 7d (from that theme's own v2) let the first theme restart before
 * the last (Aura) ever got a third-cycle slot.
 */
function pickNextReport(
  state: ModalShowState,
  purchasedReportIds: ReadonlySet<ReportId>,
  now: Date,
): AppModalType | null {
  const today = now.toISOString().slice(0, 10)
  if (today <= state.onboardingAt) return null
  if (!groupCooldownOk(state, "reports", now)) return null

  const openThemes = REPORT_THEME_PRIORITY.filter(
    (theme) => !purchasedReportIds.has(REPORT_THEME_TO_ID[theme]),
  )
  if (openThemes.length === 0) return null

  const stageOf = (theme: ReportThemeKey) => state.reportThemeStage[theme] ?? 0
  const awaitingV2 = openThemes.some((theme) => stageOf(theme) === 1)
  const allDoneV2 = openThemes.every((theme) => stageOf(theme) === 2)

  if (allDoneV2) {
    const lastV2 = latestOpenThemeV2(state, openThemes)
    if (!hoursPassed(lastV2, now, REPORT_AFTER_V2_COOLDOWN_MS)) return null
    const type = themeModalType(openThemes[0], 1)
    return typeCooldownOk(state, type, now) ? type : null
  }

  for (const theme of openThemes) {
    if (stageOf(theme) !== 0) continue
    const type = themeModalType(theme, 1)
    if (typeCooldownOk(state, type, now)) return type
  }

  if (!awaitingV2) return null

  for (const theme of openThemes) {
    if (stageOf(theme) !== 1) continue
    const type = themeModalType(theme, 2)
    if (typeCooldownOk(state, type, now)) return type
  }

  return null
}

function latestOpenThemeV2(
  state: ModalShowState,
  themes: ReportThemeKey[],
): string | undefined {
  let latest: string | undefined
  for (const theme of themes) {
    const at = state.lastShownAtByType[themeModalType(theme, 2)]
    if (!at) continue
    if (!latest || new Date(at).getTime() > new Date(latest).getTime()) {
      latest = at
    }
  }
  return latest
}

/**
 * Stars top-up cadence by balance:
 * - under 30: at most once per day (group 24h)
 * - 30+: at most once per 5 days
 * No per-variant type cooldown — that blocked daily low-balance shows.
 */
function pickStarsType(
  state: ModalShowState,
  credits: number,
  now: Date,
): AppModalType | null {
  const lastStars = state.lastShownAtByGroup.stars
  const lowBalance = credits < STARS_LOW_BALANCE_THRESHOLD
  const cooldownMs = lowBalance
    ? GROUP_COOLDOWN_MS
    : STARS_HIGH_BALANCE_COOLDOWN_MS

  if (!hoursPassed(lastStars, now, cooldownMs)) return null

  const variant = (state.starsShowCount % 2) + 1
  return variant === 1 ? "stars_special_offer_v1" : "stars_special_offer_v2"
}

/** True when stars could still become eligible once credits load. */
export function mightStarsBecomeEligible(
  state: ModalShowState,
  now = getAppNow(),
): boolean {
  // Without credits we don't know 24h vs 5d. If the shorter (daily) window
  // hasn't elapsed, stars can't show either way.
  return groupCooldownOk(state, "stars", now)
}
